import AssistChip from 'LuxKit/Chips/AssistChip'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import React, { ChangeEventHandler, RefObject, useCallback, useState } from 'react'
import { connect } from 'react-redux'
import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import BusinessTravellerSelectHotelsTravellersContent from './BusinessTravellerSelectHotelsTravellersContent'
import TextInput from 'components/Common/Form/Input/TextInput'
import { SearchMenuStates } from 'components/Search/type'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import IconButton from 'LuxKit/Button/IconButton'

interface Props {
  activeMenu: SearchMenuStates
  onClose: () => void
  onApply: () => void
  onAddEmployee: (employeeId: string) => void
  onRemoveEmployee: (employeeId: string) => void
  onAddGuest: () => void
  onRemoveGuest: () => void
  inputRef: RefObject<HTMLButtonElement | null>
  totalTravellers: number
}

interface MappedStateProps {
  travellerEmployees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
  guestCount: number
}

function BusinessTravellerSelectHotelsTravellersDesktop(props: Props & MappedStateProps) {
  const { travellerEmployees, guestCount, activeMenu, onClose, inputRef, totalTravellers, onApply, onAddEmployee, onRemoveEmployee, onAddGuest, onRemoveGuest } = props

  const [fieldActive, setFieldActive] = useState(false)

  const [searchTerm, setSearchTerm] = useState<string>('')
  const handleSearchTermChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchTerm(event.currentTarget.value)
  }, [])

  const onCloseDropdown = useCallback(() => {
    setFieldActive(false)
    onClose()
  }, [onClose, setFieldActive])

  return (
    <DropdownList
      size="M"
      open={activeMenu === SearchMenuStates.BusinessTravellerEmployeeSelect}
      onClose={onCloseDropdown}
      triggerRef={inputRef}
      anchorRef={inputRef}
      title="Who's travelling"
      subtitle={`${totalTravellers} ${totalTravellers === 1 ? 'traveller' : 'travellers'} added`}
      hasDismissButton
      portaled
      placement="bottom-start"
      width="min"
      primaryActionProps={{
        children: 'Apply',
        onClick: onApply,
      }}
      headerExtension={<Group direction="vertical" gap={12}>
        <TextInput
          type="text"
          placeholder="Add traveller"
          onChange={handleSearchTermChange}
          onFocus={() => setFieldActive(true)}
          endIcon={<>
            {!fieldActive && <LineSearchIcon />}
            {fieldActive && <IconButton kind="tertiary" horizontalOutdent="end" onClick={(e) => { e.preventDefault(); setFieldActive(false) }}><LineTimesIcon /></IconButton>}
          </>}
          noValidationSpacing
        />
        <Group direction="horizontal" gap={8} wrap="wrap">
          {travellerEmployees.map(employee => (
            <AssistChip onClick={() => onRemoveEmployee(employee.id)} key={employee.id} endIcon={<LineTimesIcon />} variant="outline" size="small">{employee.firstName} {employee.lastName}</AssistChip>
          ))}
          {Array.from({ length: guestCount }).map((_, index) => (
            <AssistChip onClick={() => onRemoveGuest()} key={index} endIcon={<LineTimesIcon />} variant="outline" size="small">Guest traveller</AssistChip>
          ))}
        </Group>
        <Group direction="horizontal" gap={12}>
          <TextButton size="medium" kind="tertiary" startIcon={<LinePlusIcon />} onClick={onAddGuest} horizontalOutdent="start">
            Add guest traveller
          </TextButton>
        </Group>
      </Group>}
    >
      <BusinessTravellerSelectHotelsTravellersContent
        inputActive={fieldActive}
        searchTerm={searchTerm}
        onAddEmployee={onAddEmployee}
        onRemoveEmployee={onRemoveEmployee}
      />
    </DropdownList>
  )
}

export default connect<MappedStateProps, {}, Props, App.State>((state) => {
  return {
    travellerEmployees: selectSelectedTravellerEmployees(state),
    guestCount: state.businessTraveller.selectedTravellerGuestCount,
  }
})(BusinessTravellerSelectHotelsTravellersDesktop)
